import React, { useEffect, useMemo, useRef, useState } from "react";

import styled from "@emotion/styled";
import snakeCase from "lodash/snakeCase";

import LoaderContainer from "shared/components/LoaderContainer";
import { Modal } from "shared/components/Modal";
import { RegularText } from "shared/components/Typography";
import { COLORS } from "shared/config/constants";
import { SUPPRESSED_FRAUD_INDICATOR_IDS } from "shared/config/fraudIndicators";
import type { OcrResult } from "shared/interfaces/api/document";
import documentsApi from "shared/lib/api/documentsApi";

// Re-exported for backwards compatibility with existing imports/tests.
// The source of truth lives in shared/config/fraudIndicators.
export { SUPPRESSED_FRAUD_INDICATOR_IDS };

const camelToSnake = (obj: any): any => {
  // Our api automatically converts the resistant.ai keys to camelCase but
  // the iframe is expecting snake case as thats what the resistant response was
  // originally. We convert here with any required exceptions.
  if (obj === null || obj === undefined) return obj;
  if (Array.isArray(obj)) return obj.map(camelToSnake);
  if (typeof obj !== "object") return obj;

  const specialCases: Record<string, string> = {
    sha256: "sha256",
  };

  return Object.keys(obj).reduce((acc, key) => {
    const snakeKey = specialCases[key] || snakeCase(key);
    const value = obj[key];
    acc[snakeKey] = camelToSnake(value);
    return acc;
  }, {} as any);
};

const cleanNullValues = (obj: any): any => {
  if (obj === null) return "";
  if (obj === undefined) return undefined;
  if (Array.isArray(obj)) return obj.map(cleanNullValues);
  if (typeof obj !== "object") return obj;

  return Object.keys(obj).reduce((acc, key) => {
    const value = obj[key];
    if (value === null) {
      acc[key] = "";
    } else if (typeof value === "object") {
      acc[key] = cleanNullValues(value);
    } else {
      acc[key] = value;
    }
    return acc;
  }, {} as any);
};

const IframeContainer = styled.div`
  width: 100%;
  height: 80vh;
  max-height: 900px;
  min-height: 700px;
  position: relative;
  overflow: hidden;

  iframe {
    width: 100%;
    height: 100%;
    border: none;
    display: block;
  }
`;

const ErrorContainer = styled.div`
  padding: 40px;
  text-align: center;
  color: ${COLORS.red};
`;

export interface FraudSignalsIframeModalProps {
  documentId: string;
  fraudData?: OcrResult[];
}

const RAI_IFRAME_URL =
  "https://assets.documents.resistant.ai/iframe-standalone/latest/";
const RAI_ORIGIN = "https://assets.documents.resistant.ai";
const MAX_RETRIES = 3;

// Drops suppressed indicators from a snake_cased fraud visualization payload
// before it is sent to the Resistant.AI iframe. Operates on `indicator_id`
// (keys are already snake_case at this point) and never mutates its input.
export const removeSuppressedIndicators = (fraudSnakeCase: any): any => {
  if (!fraudSnakeCase || !Array.isArray(fraudSnakeCase.indicators)) {
    return fraudSnakeCase;
  }
  return {
    ...fraudSnakeCase,
    indicators: fraudSnakeCase.indicators.filter(
      (indicator: any) =>
        !SUPPRESSED_FRAUD_INDICATOR_IDS.has(indicator?.indicator_id)
    ),
  };
};

export const FraudSignalsIframeModal = ({
  documentId,
  fraudData,
}: FraudSignalsIframeModalProps) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [documentBuffer, setDocumentBuffer] = useState<ArrayBuffer | null>(
    null
  );
  const [isIframeLoaded, setIsIframeLoaded] = useState(false);

  // Stable key that never changes after mount
  const iframeKeyRef = useRef(`${documentId}-${Date.now()}`);

  // Refs for tracking state without causing re-renders
  const hasSentData = useRef(false);
  const retryCount = useRef(0);

  // Store fraud data in a ref on mount to prevent prop reference changes from affecting us
  // The ModalManager's cloneDeep creates new references on every modal event
  const fraudDataRef = useRef(fraudData);

  // Memoize derived values from the stable ref - these won't change even if props do
  const {
    documentUrl,
    fraudSubmissionId,
    fraudVisualization,
    adaptiveDecisionRawResponse,
  } = useMemo(() => {
    const firstResult = fraudDataRef.current?.[0];
    return {
      documentUrl: firstResult?.url,
      fraudSubmissionId: firstResult?.fraudSubmissionId,
      fraudVisualization: firstResult?.fraudVisualizations?.[0]?.visualization,
      // Presence implies the adaptive-decisioning flag was on + Resistant
      // returned a mappable decision for this submission (document-service
      // leaves this null on flag-off or fallback; see PR #644 guard).
      adaptiveDecisionRawResponse: firstResult?.adaptiveDecisionRawResponse,
    };
  }, []); // Empty deps - only compute once on mount

  // Ref to hold the current document buffer for use in message handler
  const documentBufferRef = useRef<ArrayBuffer | null>(null);
  documentBufferRef.current = documentBuffer;

  // Stable send function stored in a ref - prevents effect re-runs
  const sendDataToIframeRef = useRef<(isRetry?: boolean) => void>(() => {});

  sendDataToIframeRef.current = (isRetry = false) => {
    if (hasSentData.current && !isRetry) return;
    if (!iframeRef.current?.contentWindow) return;

    const buffer = documentBufferRef.current;
    if (!buffer || !fraudSubmissionId) return;

    if (!fraudVisualization) {
      setHasError(true);
      setIsLoading(false);
      return;
    }

    const fraudSnakeCase = removeSuppressedIndicators(
      camelToSnake(fraudVisualization)
    );
    const cleanedFraud = cleanNullValues(fraudSnakeCase);
    const fraudString = JSON.stringify(cleanedFraud);
    const bufferCopy = buffer.slice(0);

    // Resistant.AI offline iFrame accepts an optional `decision` field
    // alongside `fraud`; it is required for customers using Adaptive
    // Decisioning (see vendor "Offline iFrame Usage Guide" section 3).
    //
    // Two transformations are always required:
    //   1. camelToSnake: our API layer camelCases the persisted response,
    //      but the iframe expects Resistant's original snake_case keys.
    //   2. decision_inputs.fraud.data: the /decision body stores it as null
    //      (Resistant expects a fetch-by-URL), but the offline iFrame can't
    //      fetch, so we inline the fraud body we already send as `fraud`.
    // cleanNullValues is intentionally NOT applied — other meaningful nulls
    // exist in the decision body that the iframe parses structurally.
    //
    // `internal_decision` (APPROVED / MANUAL_REVIEW / DECLINED) is required
    // by the iframe's DecisionSuccessSchema. It is returned by Resistant
    // only when the backend calls /decision with `include_additional_data=true`
    // (confirmed on PRODUCT-1351 2026-04-23). We trust the vendor value when
    // present; the client-side mapping below is kept only as a defensive
    // fallback for submissions stored before the backend change shipped.
    const DECISION_TO_INTERNAL: Record<string, string> = {
      LOW_RISK: "APPROVED",
      MEDIUM_RISK: "MANUAL_REVIEW",
      HIGH_RISK: "DECLINED",
    };
    let decisionString: string | undefined;
    if (adaptiveDecisionRawResponse) {
      const decisionBody = camelToSnake(adaptiveDecisionRawResponse) as Record<string, any>;
      if (decisionBody.decision_inputs?.fraud) {
        decisionBody.decision_inputs.fraud.data = cleanedFraud;
      }
      if (!decisionBody.internal_decision) {
        const wireDecision = decisionBody.decision as string | undefined;
        if (wireDecision && DECISION_TO_INTERNAL[wireDecision]) {
          decisionBody.internal_decision = DECISION_TO_INTERNAL[wireDecision];
        }
      }
      decisionString = JSON.stringify(decisionBody);
    }

    const customStyles = {
      background: {
        secondary: "#F3F4F9",
      },
    };

    const messagePayload: Record<string, unknown> = {
      fraud: fraudString,
      fileData: bufferCopy,
      locale: "en",
      style: JSON.stringify({ colors: customStyles }),
    };
    if (decisionString) {
      messagePayload.decision = decisionString;
    }

    iframeRef.current.contentWindow.postMessage(messagePayload, RAI_ORIGIN, [
      bufferCopy,
    ]);

    if (!isRetry) {
      hasSentData.current = true;
    }
    setIsLoading(false);
  };

  // Fetch document - only runs once since documentUrl is memoized
  useEffect(() => {
    if (!documentUrl) {
      setHasError(true);
      setIsLoading(false);
      return undefined;
    }

    let cancelled = false;

    const fetchDocument = async () => {
      try {
        const arrayBuffer = await documentsApi.loadFileByUrl(documentUrl);
        if (!cancelled) {
          setDocumentBuffer(arrayBuffer);
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(
          "[FraudSignalsIframeModal] Error fetching document:",
          error
        );
        if (!cancelled) {
          setHasError(true);
          setIsLoading(false);
        }
      }
    };

    fetchDocument();

    return () => {
      cancelled = true;
    };
  }, [documentUrl]);

  // Message handler - stable, no dependencies that change
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.origin !== RAI_ORIGIN) return;

      const messageType = event.data?.type;

      if (messageType === "LOAD_ERROR" || messageType === "error") {
        if (retryCount.current < MAX_RETRIES && documentBufferRef.current) {
          retryCount.current += 1;
          setTimeout(() => {
            sendDataToIframeRef.current(true);
          }, 300);
        } else {
          setHasError(true);
          setIsLoading(false);
        }
        return;
      }

      if (messageType === "LOADED" || messageType === "success") {
        setIsLoading(false);
      }
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, []); // Empty deps - handler uses refs, so it never needs to re-run

  // Send data when iframe is loaded and document is ready
  useEffect(() => {
    if (
      isIframeLoaded &&
      documentBuffer &&
      fraudSubmissionId &&
      !hasSentData.current
    ) {
      sendDataToIframeRef.current();
    }
  }, [isIframeLoaded, documentBuffer, fraudSubmissionId]);

  const handleIframeLoad = () => {
    retryCount.current = 0;
    // Small delay to ensure iframe is fully ready to receive messages
    setTimeout(() => {
      setIsIframeLoaded(true);
    }, 200);
  };

  const handleIframeError = () => {
    setHasError(true);
    setIsLoading(false);
  };

  // Validate required data on mount
  useEffect(() => {
    if (!fraudSubmissionId) {
      setHasError(true);
      setIsLoading(false);
    }
  }, [fraudSubmissionId]);

  return (
    <Modal
      title="Fraud Signal Details"
      width="95%"
      style={{ maxWidth: 1600 }}
      showSubmitButton={false}
      showCancelLink={false}
      centered
      bodyStyle={{ padding: 0 }}
    >
      <Modal.Body>
        <IframeContainer>
          {isLoading && (
            <LoaderContainer loading>
              <div
                style={{ height: "85vh", maxHeight: 1200, minHeight: 700 }}
              />
            </LoaderContainer>
          )}
          {hasError && (
            <ErrorContainer>
              <RegularText>
                {!documentUrl || !fraudSubmissionId
                  ? "Fraud signal details are not available for this document."
                  : "Failed to load fraud signal details. Please try again later."}
              </RegularText>
            </ErrorContainer>
          )}
          <iframe
            key={iframeKeyRef.current}
            ref={iframeRef}
            src={RAI_IFRAME_URL}
            title={`Fraud Signals for Document ${documentId}`}
            sandbox="allow-scripts allow-same-origin allow-forms"
            onLoad={handleIframeLoad}
            onError={handleIframeError}
            style={{ display: hasError ? "none" : "block" }}
          />
        </IframeContainer>
      </Modal.Body>
    </Modal>
  );
};

export default FraudSignalsIframeModal;
